"use client";

/**
 * Блок «Служба поддержки» в шторке своего профиля: контакты, которые участники школы
 * видят в сайдбаре и на экране теста.
 *
 * Контракт:
 *  - блок самостоятельный, как «Безопасность»: свой <form> и своя кнопка. Контакты
 *    пишутся строками school_contacts школы (PUT /schools/{id}), а не в пользователя,
 *    поэтому общий «Сохранить» профиля их не отправляет;
 *  - форма рисуется, когда пришли и школа, и её текущие строки: сохранение пересоздаёт
 *    их целиком, и без них чужие контакты школы потерялись бы;
 *  - номер — просто телефон. WhatsApp включается на нём тумблером: номер поддержки
 *    бывает и без мессенджера, поэтому без тумблера участник видит только номер;
 *  - Telegram принимает то же, что форма регистрации: ник, номер или ссылку t.me.
 *    MAX кабинетам trustexam не нужен;
 *  - пустые поля — контакта нет. Пустые все — школа возвращается к контактам по
 *    умолчанию (карта в constants/supportContacts);
 *  - кому показывать блок, решает родитель: менеджеру и администратору бренда trustexam.
 */

import * as React from "react";
import { useTranslations } from "next-intl";
import { toast } from "sonner";
import { MessageCircle } from "lucide-react";

import {
  useGetMySchoolContactsQuery,
  useGetUserQuery,
  useUpdateMySchoolContactsMutation,
} from "@/api/auth/authPrivateApi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PhoneInput } from "@/components/ui/phone-input";
import { Switch } from "@/components/ui/switch";
import {
  readSchoolSupportContacts,
  withSchoolSupportContacts,
  type SchoolContactRow,
} from "@/constants/supportContacts";
import type { UserEntity } from "@/types/canonical";

type FormProps = {
  school: UserEntity["school"];
  rows: SchoolContactRow[];
};

const SupportContactsForm: React.FC<FormProps> = ({ school, rows }) => {
  const t = useTranslations();
  const [updateContacts, { isLoading }] = useUpdateMySchoolContactsMutation();

  const [contacts, setContacts] = React.useState(() => readSchoolSupportContacts(rows));

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    try {
      await updateContacts({
        school,
        contacts: withSchoolSupportContacts(rows, contacts),
      }).unwrap();
      toast.success(t("ui-notification-changes-saved"));
    } catch {
      toast.error(t("label-technical-support-save-failed"));
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4 border-t pt-6">
      <div className="flex items-start gap-3">
        <MessageCircle size={18} className="mt-1 shrink-0 text-muted-foreground" />
        <div>
          <div className="font-semibold">{t("label-technical-support")}</div>
          <div className="text-sm text-muted-foreground">
            {t("label-technical-support-school-hint")}
          </div>
        </div>
      </div>

      <div className="space-y-2">
        <label className="text-sm font-medium block">{t("label-phone")}</label>
        <PhoneInput
          value={contacts.phone}
          onChange={(next) =>
            setContacts((prev) => ({ ...prev, phone: String(next ?? "") }))
          }
          international
          disabled={isLoading}
        />
        {/* Название мессенджера одинаково во всех локалях — намеренно не ключ i18n. */}
        <label className="flex items-center gap-3 text-sm">
          <Switch
            checked={contacts.whatsapp}
            onCheckedChange={(whatsapp) => setContacts((prev) => ({ ...prev, whatsapp }))}
            disabled={isLoading || !contacts.phone}
          />
          WhatsApp
        </label>
      </div>

      <div className="space-y-1">
        <label className="text-sm font-medium block">Telegram</label>
        <Input
          value={contacts.telegram}
          onChange={(e) =>
            setContacts((prev) => ({ ...prev, telegram: e.target.value }))
          }
          placeholder={t("label-technical-support-telegram-placeholder")}
          disabled={isLoading}
        />
        <div className="text-xs text-muted-foreground">
          {t("ui-telegram-username-hint")}
        </div>
      </div>

      <div>
        <Button
          type="submit"
          variant="outline"
          className="rounded-sm bg-white px-5"
          disabled={isLoading}
        >
          {isLoading ? t("ui-saving") : t("btn-save")}
        </Button>
      </div>
    </form>
  );
};

const SchoolSupportContactsSection: React.FC = () => {
  /* /user.json уже в кэше из providers; строки контактов — отдельный GET /school.json. */
  const { data: me } = useGetUserQuery();
  const { data: rows } = useGetMySchoolContactsQuery();

  if (!me?.school || !rows) return null;

  return <SupportContactsForm school={me.school} rows={rows} />;
};

export default SchoolSupportContactsSection;
